import React, { useCallback, useEffect, useMemo, useRef, useState, useId } from 'react'
import type { Dispatch, SetStateAction, SyntheticEvent } from 'react'

import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import { Autocomplete, CircularProgress, TextField } from '@mui/material'
import { createFilterOptions } from '@mui/material/Autocomplete'
import { useAuthContext } from 'AuthProvider'
import clsx from 'clsx'
import { useItem } from 'hooks/useItems'
import _ from 'lodash'
import { SelectFormControl } from 'style'
import 'utils/mst.scss'

import type { PopperProps } from '@mui/material'
import type { FilterOptionsState } from '@mui/material/useAutocomplete'
import type { MstItem } from 'api-client'
import type { AutocompleteItem } from 'hooks/useItems'

/** Extended option when grouping by supplier (商品マスタ item API). */
type ItemAutocompleteOption = AutocompleteItem & {
  groupLabel?: string
  itemOptionLabel?: string
}

function resolveMstItemFromAutocompleteOption(
  item: AutocompleteItem,
  itemMapByOrgCode: Map<string, MstItem>,
  itemMapByItemCode: Map<string, MstItem>
): MstItem | undefined {
  if (item.orgItemCode != null) {
    const byOrg = itemMapByOrgCode.get(item.orgItemCode)
    if (byOrg) {
      return byOrg
    }
  }
  if (item.itemCode != null) {
    return itemMapByItemCode.get(item.itemCode)
  }
  return undefined
}

interface SelectItemProps {
  trCompanyId: number | undefined
  itemCode?: string | null | undefined
  orgItemCode?: string | null | undefined
  setItemCode?: Dispatch<SetStateAction<string | null | undefined>>
  setOrgItemCode?: Dispatch<SetStateAction<string | null | undefined>>
  setItemInfo: Dispatch<SetStateAction<MstItem | undefined>>
  isRequred: boolean
  setIsValid: Dispatch<SetStateAction<boolean>>
  enabled?: boolean
  isMultiple?: boolean
  setItemCodeList?: Dispatch<SetStateAction<string[]>>
  setOrgItemCodeList?: Dispatch<SetStateAction<string[]>>
  setItemInfoList?: Dispatch<SetStateAction<MstItem[]>>
  // Filter props
  orgSupplierId?: string | null | undefined
  orgSupplierIdList?: string[]
  // Initial list from URL/localStorage for multi mode display
  itemCodeList?: string[]
  orgItemCodeList?: string[]
  startSelectTop?: boolean
  /** Group dropdown by `[org_supplier_id]supplier_name` (item API); rows `[orgItemCode]name`. Default true; set false to flat list. */
  groupBySupplierItems?: boolean
  /** Multiple: increment to close dropdown (e.g. when supplier chips change). */
  closeDropdownSignal?: number
}

function SelectItem({
  trCompanyId,
  itemCode,
  orgItemCode,
  setItemCode,
  setOrgItemCode,
  setItemInfo,
  isRequred,
  setIsValid,
  enabled,
  isMultiple,
  setItemCodeList,
  setOrgItemCodeList,
  setItemInfoList,
  orgSupplierId,
  orgSupplierIdList,
  itemCodeList: itemCodeListProp,
  orgItemCodeList: orgItemCodeListProp,
  startSelectTop,
  groupBySupplierItems = true,
  closeDropdownSignal = 0,
}: SelectItemProps) {
  const { loginUserInfo } = useAuthContext()
  const uniqueId = useId()
  const { items, fetchItems, isLoading: itemSearchIsLoading } = useItem()
  const [trValue, setTrValue] = useState<AutocompleteItem | null | undefined>(undefined)
  const [trValueList, setTrValueList] = useState<AutocompleteItem[]>([])
  const itemAutocompleteRef = useRef<HTMLDivElement | null>(null)
  const isFirstRenderRef = useRef(true)
  const prevTrCompanyIdRef = useRef<number | undefined>(undefined)

  const autocompleteItems = useMemo<AutocompleteItem[]>(
    () =>
      (items?.list ?? []).map((value) => ({
        label: value.itemName ?? null,
        orgItemCode: value.orgItemCode ?? null,
        itemCode: value.itemCode ?? null,
      })),
    [items?.list]
  )

  // Create Map for faster lookup
  const itemMapByOrgCode = useMemo(() => {
    const map = new Map<string, MstItem>()
    items?.list.forEach((item) => {
      if (item.orgItemCode != null) {
        map.set(item.orgItemCode, item)
      }
    })
    return map
  }, [items?.list])

  const itemMapByItemCode = useMemo(() => {
    const map = new Map<string, MstItem>()
    items?.list.forEach((item) => {
      if (item.itemCode != null) {
        map.set(item.itemCode, item)
      }
    })
    return map
  }, [items?.list])

  useEffect(() => {
    if (!isFirstRenderRef.current) {
      // Only clear itemCode/orgItemCode if trCompanyId actually changed (not just on re-render)
      const trCompanyIdChanged = prevTrCompanyIdRef.current !== trCompanyId

      if (trCompanyIdChanged) {
        if (setItemCode) {
          setItemCode(undefined)
          setTrValue(undefined)
        } else if (setOrgItemCode) {
          setOrgItemCode(undefined)
          setTrValue(undefined)
        }

        // Clear multiple selection values
        if (isMultiple) {
          setTrValueList([])
          setItemCodeList?.([])
          setOrgItemCodeList?.([])
          setItemInfoList?.([])
        }
      }
    }

    prevTrCompanyIdRef.current = trCompanyId

    if (trCompanyId === undefined || trCompanyId === null) {
      fetchItems({ enabled: enabled ?? undefined, companyId: loginUserInfo!.companyId }, { useCache: true })
    } else {
      fetchItems({ enabled: enabled ?? undefined, companyId: trCompanyId }, { useCache: true })
    }
  }, [trCompanyId, enabled, isMultiple, setItemCode, setOrgItemCode, setItemCodeList, setOrgItemCodeList, setItemInfoList, loginUserInfo, fetchItems])

  useEffect(() => {
    // itemCode/orgItemCodeが空の場合はクリアにする
    if (!isMultiple) {
      if (setItemCode && !setOrgItemCode) {
        if (itemCode === undefined && trValue) {
          setTrValue(null)
        }
      } else if (setOrgItemCode && !setItemCode) {
        if (orgItemCode === undefined && trValue) {
          setTrValue(null)
        }
      }
    }
    // Multiple selection mode doesn't need this clear logic as it's handled differently
  }, [itemCode, orgItemCode, trValue, isMultiple, setItemCode, setOrgItemCode])

  // 必須チェック処理
  useEffect(() => {
    // 必須チェックを上位コンポーネントに通知
    const isValid = !isMultiple ? !!trValue : trValueList.length > 0
    setIsValid(!isValid)
  }, [trValue, trValueList, isMultiple, setIsValid])

  useEffect(() => {
    // 商品APIが呼び出されレスポンスが返ってきた場合のみ実行
    if (items?.list.length) {
      if (!isMultiple) {
        // Single selection mode
        if (autocompleteItems.length > 0) {
          if (setItemCode && itemCode) {
            // itemCode may be duplicated across different orgItemCode values.
            // Prefer resolving by orgItemCode when provided.
            if (orgItemCode) {
              setTrValue(_.find(autocompleteItems, { itemCode, orgItemCode }) ?? _.find(autocompleteItems, { orgItemCode }) ?? _.find(autocompleteItems, { itemCode }))
            } else {
              setTrValue(_.find(autocompleteItems, { itemCode }))
            }
          } else if (setOrgItemCode && orgItemCode) {
            setTrValue(_.find(autocompleteItems, { orgItemCode }))
          }
        }
      }
      if (isMultiple) {
        // Multiple: sync from parent itemCodeList (URL/localStorage) when we have options
        const codesForInit = (orgItemCodeListProp?.length ? orgItemCodeListProp : itemCodeListProp) ?? []
        if (codesForInit.length && autocompleteItems.length > 0) {
          const matched = codesForInit
            .map((code) => _.find(autocompleteItems, { orgItemCode: code }) ?? _.find(autocompleteItems, { itemCode: code }))
            .filter((item): item is AutocompleteItem => item != null)
          setTrValueList(matched)
        } else if (isFirstRenderRef.current) {
          setTrValueList([])
        }
      }
    }

    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false
    }
  }, [items, autocompleteItems, isMultiple, itemCode, orgItemCode, setItemCode, setOrgItemCode, itemCodeListProp, orgItemCodeListProp])

  // When parent clears itemCodeList in multiple mode, also clear local selection list
  useEffect(() => {
    const hasAnyInitCodes = (itemCodeListProp?.length ?? 0) > 0 || (orgItemCodeListProp?.length ?? 0) > 0
    if (isMultiple && !hasAnyInitCodes) {
      setTrValueList([])
    }
  }, [isMultiple, itemCodeListProp, orgItemCodeListProp])

  useEffect(() => {
    if (!isMultiple || closeDropdownSignal <= 0) {
      return
    }
    const input = itemAutocompleteRef.current?.querySelector('input')
    input?.blur()
  }, [isMultiple, closeDropdownSignal])

  useEffect(() => {
    if (!isMultiple && items?.list) {
      if (setItemCode && itemCode) {
        setItemInfo(_.find(items.list, { itemCode }))
      } else if (setOrgItemCode && orgItemCode) {
        setItemInfo(_.find(items.list, { orgItemCode }))
      }
    }
  }, [items, itemCode, orgItemCode, isMultiple, setItemCode, setOrgItemCode, setItemInfo])

  // Helper function to handle single selection
  const handleSingleSelection = useCallback((selectedValue: AutocompleteItem | null) => {
    if (selectedValue) {
      setTrValue(selectedValue)

      if (setItemCode && selectedValue.itemCode != null) {
        setItemCode(selectedValue.itemCode)
        const itemInfo = itemMapByItemCode.get(selectedValue.itemCode)
        if (itemInfo) {
          setItemInfo(itemInfo)
        }
      }

      if (setOrgItemCode && selectedValue.orgItemCode != null) {
        setOrgItemCode(selectedValue.orgItemCode)
        const itemInfo = itemMapByOrgCode.get(selectedValue.orgItemCode)
        if (itemInfo) {
          setItemInfo(itemInfo)
        }
      }

      // Always set multiple values for consistency
      const itemCodes = selectedValue.itemCode != null ? [selectedValue.itemCode] : []
      const orgItemCodes = selectedValue.orgItemCode != null ? [selectedValue.orgItemCode] : []
      let itemInfo: MstItem | undefined
      if (selectedValue.itemCode != null) {
        itemInfo = itemMapByItemCode.get(selectedValue.itemCode)
      } else if (selectedValue.orgItemCode != null) {
        itemInfo = itemMapByOrgCode.get(selectedValue.orgItemCode)
      }
      const itemInfos = itemInfo ? [itemInfo] : []

      setItemCodeList?.(itemCodes)
      setOrgItemCodeList?.(orgItemCodes)
      setItemInfoList?.(itemInfos)
    } else {
      setTrValue(null)
      if (setItemCode) setItemCode(undefined)
      if (setOrgItemCode) setOrgItemCode(undefined)
      setItemInfo(undefined)
      setItemCodeList?.([])
      setOrgItemCodeList?.([])
      setItemInfoList?.([])
    }
  }, [setItemCode, setOrgItemCode, setItemInfo, setItemCodeList, setOrgItemCodeList, setItemInfoList, itemMapByItemCode, itemMapByOrgCode])

  // Helper function to handle multiple selection
  const handleMultipleSelection = useCallback((selectedValues: AutocompleteItem[]) => {
    setTrValueList(selectedValues)

    const itemCodes = selectedValues
      .map(v => v.itemCode)
      .filter((code): code is string => code !== null && code !== undefined)
    const orgItemCodes = selectedValues
      .map(v => v.orgItemCode)
      .filter((code): code is string => code !== null && code !== undefined)
    const itemInfos = selectedValues
      .map(v => {
        if (v.itemCode) {
          return itemMapByItemCode.get(v.itemCode)
        }
        if (v.orgItemCode) {
          return itemMapByOrgCode.get(v.orgItemCode)
        }
        return undefined
      })
      .filter((info): info is MstItem => info !== undefined)

    setItemCodeList?.(itemCodes)
    setOrgItemCodeList?.(orgItemCodes)
    setItemInfoList?.(itemInfos)

    // Also set single values for backward compatibility
    if (selectedValues.length > 0) {
      const firstValue = selectedValues[0]
      if (setItemCode && firstValue.itemCode != null) {
        setItemCode(firstValue.itemCode)
        const itemInfo = itemMapByItemCode.get(firstValue.itemCode)
        if (itemInfo) {
          setItemInfo(itemInfo)
        }
      } else if (setOrgItemCode && firstValue.orgItemCode != null) {
        setOrgItemCode(firstValue.orgItemCode)
        const itemInfo = itemMapByOrgCode.get(firstValue.orgItemCode)
        if (itemInfo) {
          setItemInfo(itemInfo)
        }
      }
    } else {
      if (setItemCode) setItemCode(undefined)
      if (setOrgItemCode) setOrgItemCode(undefined)
      setItemInfo(undefined)
    }
  }, [setItemCode, setOrgItemCode, setItemInfo, setItemCodeList, setOrgItemCodeList, setItemInfoList, itemMapByItemCode, itemMapByOrgCode])

  const getOptionValue = useCallback((option: AutocompleteItem) => option.orgItemCode ?? option.itemCode ?? '', [])

  const getOptionLabel = useCallback((option: AutocompleteItem) => {
    const extended = option as ItemAutocompleteOption
    if (extended.itemOptionLabel) {
      return extended.itemOptionLabel
    }
    const name = option.label ?? ''
    const id = option.orgItemCode ?? option.itemCode ?? ''
    return id ? `[${id}]${name}` : name
  }, [])

  const defaultFilterItemOptions = useMemo(
    () => createFilterOptions<AutocompleteItem>(),
    [],
  )

  /** When grouping by supplier: filter both item and group label text `[org_supplier_id]supplier_name`. */
  const filterItemOptions = useCallback(
    (options: AutocompleteItem[], state: FilterOptionsState<AutocompleteItem>) => {
      if (!groupBySupplierItems) {
        return defaultFilterItemOptions(options, state)
      }
      const q = state.inputValue.trim().toLowerCase()
      if (!q) {
        return options
      }
      return options.filter((opt) => {
        const ext = opt as ItemAutocompleteOption
        const itemText = (ext.itemOptionLabel ?? getOptionLabel(opt)).toLowerCase()
        const supplierText = (ext.groupLabel ?? '').toLowerCase()
        return itemText.includes(q) || supplierText.includes(q)
      })
    },
    [groupBySupplierItems, defaultFilterItemOptions, getOptionLabel],
  )

  const handleAutocompleteChange = useCallback((e: SyntheticEvent<Element, Event>, selectedValue: any) => {
    handleSingleSelection(selectedValue as AutocompleteItem | null)
  }, [handleSingleSelection])

  const handleItemOptionEqualToValue = useCallback((option: AutocompleteItem, value: AutocompleteItem) => {
    if (setItemCode) {
      return option.itemCode === value.itemCode && option.orgItemCode === value.orgItemCode
    }
    return option.orgItemCode === value.orgItemCode
  }, [setItemCode])

  const handleItemRenderInput = useCallback((params: any) => {
    const hasError = isRequred && !trValue
    return (
      <TextField
        label="商品"
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...params}
        className={clsx('select-item-text-field')}
        InputProps={{
          ...params.InputProps,
          endAdornment: (
            <>
              {itemSearchIsLoading ? (
                <CircularProgress color="inherit" size={20} />
              ) : null}
              {params.InputProps.endAdornment}
            </>
          ),
          // readOnly: true,
          // tabIndex: -1,
        }}
        error={hasError}
        // helperText={hasError ? '必須' : ''}
        required={isRequred}
      />
    )
  }, [itemSearchIsLoading, isRequred, trValue])

  /** Empty = show all items (e.g. supplier not selected yet). */
  const activeOrgSupplierIds = useMemo(() => {
    if (isMultiple) {
      return (orgSupplierIdList ?? []).filter((id): id is string => id != null && id !== '')
    }
    if (orgSupplierId) {
      return [orgSupplierId]
    }
    return []
  }, [isMultiple, orgSupplierIdList, orgSupplierId])

  const supplierIdSet = useMemo(() => new Set(activeOrgSupplierIds), [activeOrgSupplierIds])

  const filteredItems = useMemo(() => {
    const options = autocompleteItems
    if (supplierIdSet.size === 0) {
      return options
    }
    return options.filter((item) => {
      const row = resolveMstItemFromAutocompleteOption(item, itemMapByOrgCode, itemMapByItemCode)
      return row?.orgSupplierId != null && supplierIdSet.has(row.orgSupplierId)
    })
  }, [autocompleteItems, itemMapByOrgCode, itemMapByItemCode, supplierIdSet])

  /** Grouped options from 商品マスタ item list; sorted for MUI groupBy. */
  const displayItemOptions = useMemo((): AutocompleteItem[] => {
    if (!groupBySupplierItems) {
      return filteredItems
    }
    if (!items?.list?.length) {
      return filteredItems
    }
    let rows = items.list
    if (supplierIdSet.size > 0) {
      rows = rows.filter((i) => i.orgSupplierId != null && supplierIdSet.has(i.orgSupplierId))
    }
    return rows
      .map((item): ItemAutocompleteOption => {
        const orgId = item.orgSupplierId ?? ''
        const supplierName = item.supplierName ?? ''
        return {
          label: item.itemName ?? null,
          orgItemCode: item.orgItemCode ?? null,
          itemCode: item.itemCode ?? null,
          groupLabel: orgId ? `[${orgId}]${supplierName}` : supplierName,
          itemOptionLabel: item.orgItemCode
            ? `[${item.orgItemCode}]${item.itemName ?? ''}`
            : (item.itemName ?? ''),
        }
      })
      .sort((a, b) => {
        const groupCompare = (a.groupLabel ?? '').localeCompare(b.groupLabel ?? '')
        if (groupCompare !== 0) return groupCompare
        return (a.itemOptionLabel ?? '').localeCompare(b.itemOptionLabel ?? '')
      })
  }, [groupBySupplierItems, filteredItems, items?.list, supplierIdSet])

  const autocompletePopperSlotProps = useMemo(
    () => ({
      popper: {
        placement: (startSelectTop ? 'top-start' : 'bottom-start') as NonNullable<PopperProps['placement']>,
        className: clsx(
          'select-item-autocomplete-popper',
          groupBySupplierItems && 'select-item-autocomplete-popper--grouped',
        ),
      },
    }),
    [startSelectTop, groupBySupplierItems],
  )

  return (
    <SelectFormControl isMultiple={isMultiple}>
      {isMultiple ? (
        <Autocomplete
          ref={itemAutocompleteRef}
          multiple
          disableCloseOnSelect
          limitTags={1}
          id={`item-select-${uniqueId}`}
          sx={{ width: '100%' }}
          value={trValueList}
          options={displayItemOptions}
          loading={itemSearchIsLoading}
          disabled={itemSearchIsLoading}
          getOptionLabel={getOptionLabel}
          filterOptions={filterItemOptions}
          isOptionEqualToValue={(option, value) => {
            if (setItemCode) {
              return option.itemCode === value.itemCode && option.orgItemCode === value.orgItemCode
            }
            return option.orgItemCode === value.orgItemCode
          }}
          onChange={(_e, selectedValues) => {
            handleMultipleSelection(selectedValues)
          }}
          renderOption={(props, option, { selected }) => {
            const { key, ...optionProps } = props // eslint-disable-line react/prop-types
            const SelectionIcon = selected ? CheckBoxIcon : CheckBoxOutlineBlankIcon
            const row = option as ItemAutocompleteOption
            return (
              /* eslint-disable-next-line react/jsx-props-no-spreading */
              <li key={key} {...optionProps}>
                <SelectionIcon
                  fontSize="small"
                  style={{ marginRight: 8, padding: 9, boxSizing: 'content-box' }}
                />
                {row.itemOptionLabel ?? getOptionLabel(option)}
              </li>
            )
          }}
          groupBy={groupBySupplierItems ? (option) => (option as ItemAutocompleteOption).groupLabel ?? '' : undefined}
          renderGroup={
            groupBySupplierItems
              ? (params) => (
                  <li key={params.key}>
                    <div className={clsx('MuiAutocomplete-groupLabel', 'select-item-group-label')}>{params.group}</div>
                    <ul className="MuiAutocomplete-groupUl">{params.children}</ul>
                  </li>
                )
              : undefined
          }
          renderInput={(params) => {
            const hasError = isRequred && trValueList.length === 0
            return (
              <TextField
                label="商品"
                placeholder="商品"
                /* eslint-disable-next-line react/jsx-props-no-spreading */
                {...params}
                className={clsx('select-item-text-field')}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {itemSearchIsLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                error={hasError}
                // helperText={hasError ? '必須' : ''}
                required={isRequred}
              />
            )
          }}
          slotProps={autocompletePopperSlotProps}
        />
      ) : (
        <Autocomplete
          disablePortal
          id="item"
          value={trValue ?? null}
          options={displayItemOptions}
          getOptionLabel={getOptionLabel}
          filterOptions={filterItemOptions}
          loading={itemSearchIsLoading}
          disabled={itemSearchIsLoading}
          freeSolo={false}
          selectOnFocus={false}
          clearOnBlur={false}
          handleHomeEndKeys
          onChange={handleAutocompleteChange}
          isOptionEqualToValue={handleItemOptionEqualToValue}
          groupBy={groupBySupplierItems ? (option) => (option as ItemAutocompleteOption).groupLabel ?? '' : undefined}
          renderGroup={
            groupBySupplierItems
              ? (params) => (
                  <li key={params.key}>
                    <div className={clsx('MuiAutocomplete-groupLabel', 'select-item-group-label')}>{params.group}</div>
                    <ul className="MuiAutocomplete-groupUl">{params.children}</ul>
                  </li>
                )
              : undefined
          }
          renderOption={
            groupBySupplierItems
              ? (props, option) => {
                  // eslint-disable-next-line react/prop-types
                  const { key, ...optionProps } = props
                  const row = option as ItemAutocompleteOption
                  return (
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    <li key={key} {...optionProps}>
                      {row.itemOptionLabel ?? getOptionLabel(option)}
                    </li>
                  )
                }
              : undefined
          }
          renderInput={handleItemRenderInput}
          slotProps={autocompletePopperSlotProps}
        />
      )}
    </SelectFormControl>
  )
}

export default SelectItem

